/**
 * Profile photos are cropped square and shrunk in the browser before upload (decision 2026-09-23 #18).
 *
 * The server has no image library, so this is the only place a photo is resized. A 12-megapixel phone photo
 * leaves here as a JPEG of about 50–100 KB, which is what makes an upload bearable over 3G. Re-encoding also
 * turns an iPhone's HEIC into a JPEG the server accepts, wherever the browser can decode it.
 */

/** JPEG quality for the re-encoded photo: small enough for 3G, with no visible blocking at avatar size. */
const JPEG_QUALITY = 0.85;

/** JPEG has no transparency, so a transparent PNG is flattened onto white rather than black. */
const BACKGROUND = '#ffffff';

/** The centred square cut from an image, and the size it is drawn at. */
export interface SquareCrop {
  /** Where the square starts, in source pixels. */
  sx: number;
  sy: number;
  /** The square's side in the source. */
  side: number;
  /** The side it is drawn at: the source's own side, capped at `maxSide`. A photo is never enlarged. */
  target: number;
}

/** The largest centred square in a `width` × `height` image, drawn at no more than `maxSide`. */
export function squareCrop(width: number, height: number, maxSide: number): SquareCrop {
  const side = Math.max(0, Math.min(width, height));
  return {
    sx: Math.floor((width - side) / 2),
    sy: Math.floor((height - side) / 2),
    side,
    target: Math.min(side, maxSide),
  };
}

/** The browser could not decode or re-encode the file, so it cannot be used as a photo. */
export class UnreadablePhotoError extends Error {
  constructor() {
    super('The file could not be read as a photo.');
    this.name = 'UnreadablePhotoError';
  }
}

/** How a decoded image is laid onto the canvas: the canvas's size, and the drawing. */
export interface Redraw {
  width: number;
  height: number;
  draw: (context: CanvasRenderingContext2D) => void;
}

/**
 * Decodes an image file into an element a canvas can draw. The object URL is released once it has decoded:
 * the element keeps its pixels.
 *
 * @throws UnreadablePhotoError when the browser cannot decode the file (HEIC in Chrome, a PDF, a broken file).
 */
export async function decodeImage(file: Blob): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.src = url;
    await image.decode().catch(() => { throw new UnreadablePhotoError(); });
    return image;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Draws `drawing` onto a new canvas and encodes it. Re-encoding through a canvas is what strips a photo's
 * metadata, its location included, because a canvas holds pixels only.
 *
 * @throws UnreadablePhotoError when there is nothing to draw, or the browser cannot draw or encode it.
 */
export async function encodeDrawing(drawing: Redraw, type: string, quality?: number): Promise<Blob> {
  const { width, height, draw } = drawing;
  if (width === 0 || height === 0) throw new UnreadablePhotoError();

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  try {
    const context = canvas.getContext('2d');
    if (!context) throw new UnreadablePhotoError();
    draw(context);

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new UnreadablePhotoError())), type, quality);
    });
  } finally {
    releaseCanvas(canvas);
  }
}

/**
 * Gives a canvas's memory back at once. iPhone Safari caps the memory all canvases on a page may hold, and
 * frees a canvas's share only when it is collected; a canvas shrunk to nothing frees it now.
 */
export function releaseCanvas(canvas: HTMLCanvasElement): void {
  canvas.width = 0;
  canvas.height = 0;
}

/**
 * Decodes an image file, draws it as `layout` says and re-encodes the canvas. What a photo and a logo share;
 * they differ only in the layout and the encoding.
 *
 * @throws UnreadablePhotoError when the browser cannot decode the file, or the layout leaves nothing to draw.
 */
export async function redraw(
  file: Blob,
  layout: (image: HTMLImageElement) => Redraw,
  type: string,
  quality?: number,
): Promise<Blob> {
  return encodeDrawing(layout(await decodeImage(file)), type, quality);
}

/**
 * Decodes an image file, crops the centred square and re-encodes it as a JPEG of at most `maxSide` square:
 * `media.profilePhotoMaximumEdge` from `useLimits`.
 *
 * @throws UnreadablePhotoError when the browser cannot decode the file (HEIC in Chrome, a PDF, a broken file).
 */
export function toProfilePhoto(file: Blob, maxSide: number): Promise<Blob> {
  return redraw(file, (image) => {
    const crop = squareCrop(image.naturalWidth, image.naturalHeight, maxSide);
    return {
      width: crop.target,
      height: crop.target,
      draw: (context) => {
        context.fillStyle = BACKGROUND;
        context.fillRect(0, 0, crop.target, crop.target);
        context.drawImage(image, crop.sx, crop.sy, crop.side, crop.side, 0, 0, crop.target, crop.target);
      },
    };
  }, 'image/jpeg', JPEG_QUALITY);
}
